import type { ApexOptions } from 'apexcharts'
import { format } from 'date-fns-tz'
import { cloneDeep } from 'lodash'

import type { VariableData, XAxisAnnotationType } from '@/types/Chart'
import gradientCalc from '@/utils/gradientCalc'

// `language` is not part of ApexOptions but is carried along with the options for the chart components
export type WeatherChartOptions = ApexOptions & { language?: string }

export class Chart {
  public variableData: VariableData
  public chartOptions: WeatherChartOptions
  private translate: (v: string) => string
  private language: string
  private theme: string

  private labelsBackgroundColor = 'rgb(var(--v-theme-dark-color))'
  private labelsTextColor: Record<string, string> = {
    dark: '#c0bfbf',
    light: '#636363'
  }
  private currentDayColor: Record<string, string> = {
    dark: '#c0bfbf',
    light: '#636363'
  }
  private highlightColor = 'rgb(var(--v-theme-grey-color), 0.8)'

  public constructor(variableData: VariableData, translateFunction: (v: string) => string, theme: string, language: string) {
    this.variableData = cloneDeep(variableData)
    this.translate = translateFunction
    this.language = language
    this.theme = theme
    this.chartOptions = this.getTemplateChartOptions(variableData, language)
  }

  public buildChartData(): void {
    this.chartOptions.annotations?.xaxis?.push(...this.generateWeekDaysAnnotations())
    this.chartOptions.annotations?.xaxis?.push(...this.generateDatesAnnotations())
    this.chartOptions.annotations?.xaxis?.push(...this.generateStatesAnnotations())
    this.chartOptions.annotations?.xaxis?.push(this.generateTodayHighlightAnnotation())

    // Add one more date by both sides
    const minDate = new Date(this.variableData.times[0])
    const maxDate = new Date(this.variableData.times[this.variableData.times.length - 1])

    minDate.setDate(minDate.getDate() - 1)
    maxDate.setDate(maxDate.getDate() + 1)

    if (this.chartOptions.xaxis) {
      this.chartOptions.xaxis.categories = [
        minDate.getTime(),
        ...(this.chartOptions.xaxis.categories as (string | number)[]),
        maxDate.getTime()
      ]
    }

    // Add one more value by both sides
    this.variableData.values = [this.variableData.values[0]].concat(this.variableData.values)
    this.variableData.values.push(this.variableData.values[this.variableData.values.length - 1])
  }
  public updateTheme(theme: string): void {
    console.info(`Updated theme ${theme}`)
    this.theme = theme
    if (this.chartOptions.annotations && this.chartOptions.annotations.xaxis) {
      this.chartOptions.annotations.xaxis.forEach((annotationXaxis: XAxisAnnotationType) => {
        // if (annotationXaxis.label && annotationXaxis.label.style && annotationXaxis.label.style.background) {
        //   annotationXaxis.label.style.background = this.labelsBackgroundColor[this.theme]
        // }
        if (annotationXaxis.name !== 'state') {
          if (annotationXaxis.label && annotationXaxis.label.style && annotationXaxis.label.style.color) {
            annotationXaxis.label.style.color = this.labelsTextColor[this.theme]
          }
        }
      })
    }
  }

  public updateLanguage(language: string, translate: (v: string) => string): void {
    this.translate = translate
    this.language = language
    this.chartOptions.annotations?.xaxis?.forEach((element) => {
      const castedAnotation = element as XAxisAnnotationType
      if (castedAnotation.name == 'week_day' && castedAnotation.label != null) {
        castedAnotation.label.text = this.getWeekDay(castedAnotation.time ?? '1970-01-01', this.language)
      } else if (castedAnotation.name === 'state') {
        if (element.label !== undefined) {
          element.label.text = castedAnotation.label_value ? this.translate(castedAnotation.label_value) : ''
        }
      }
    })
  }

  private getTemplateChartOptions(variableData: VariableData, language: string): WeatherChartOptions {
    return {
      chart: {
        id: `weather_chart_${variableData.id}`,
        height: 265,
        type: 'line',
        background: 'rgb(var(--v-theme-dark-color))',
        zoom: { enabled: false },
        foreColor: this.currentDayColor[this.theme],
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: false,
            delay: 0
          },
          dynamicAnimation: {
            enabled: true,
            speed: 800
          }
        }
      },
      colors: [],
      dataLabels: {
        enabled: false,
        enabledOnSeries: []
      },
      stroke: {
        curve: ['straight'],
        dashArray: [0]
      },
      title: {
        text: undefined,
        align: 'center'
      },
      grid: {
        show: false
      },
      markers: {
        size: [0],
        shape: ['square'],
        hover: {
          size: 0
        },
        showNullDataPoints: false
      },
      xaxis: {
        type: 'datetime',
        categories: this.getTimes(variableData.times),
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: [
        {
          show: false,
          axisTicks: { show: false },
          axisBorder: { show: false },
          tickAmount: 9,
          reversed: false,
          min: 0,
          max: 8
        }
      ],
      theme: {},
      annotations: {
        xaxis: [],
        yaxis: []
      },
      tooltip: {
        enabled: false
      },
      language: language,
      legend: {
        show: false,
        showForSingleSeries: false
      },
      fill: {
        type: 'gradient',
        gradient: {
          type: 'vertical',
          colorStops: this.getGradientData(variableData)
        }
      }
    }
  }

  private getTimes(times: string[]) {
    return times.map((x) => new Date(x).getTime())
  }

  private getGradientData(variableData: VariableData) {
    return gradientCalc.getColorStops(variableData.values, variableData.colors)
  }

  private getWeekDay(stringDate: string, language = 'en-US'): string {
    const date = new Date(stringDate)
    const weekDay = date.toLocaleDateString(language, { weekday: 'short' })
    return weekDay.charAt(0).toUpperCase() + weekDay.slice(1, 4)
  }

  private generateWeekDaysAnnotations(): XAxisAnnotationType[] {
    return this.variableData.times.map((x) => {
      return {
        x: new Date(`${x}T00:00`).getTime(),
        borderColor: 'transparent',
        name: 'week_day',
        time: x,
        label: {
          borderColor: '#00E396',
          borderWidth: 0,
          orientation: 'horizontal',
          text: this.getWeekDay(x, this.language),
          offsetY: 10,
          style: {
            background: this.labelsBackgroundColor,
            color: this.labelsTextColor[this.theme],
            fontSize: '11px'
          }
        }
      }
    })
  }

  private getDateFormat(stringDate: string, dateFormat = 'dd/MM'): string {
    return format(new Date(stringDate), dateFormat)
  }

  private generateDatesAnnotations(): XAxisAnnotationType[] {
    return this.variableData.times.map((x) => {
      return {
        x: new Date(`${x}T00:00`).getTime(),
        borderColor: 'transparent',
        label: {
          borderColor: '#00E396',
          borderWidth: 0,
          orientation: 'horizontal',
          text: this.getDateFormat(x),
          offsetY: 25,
          style: {
            background: this.labelsBackgroundColor,
            color: this.labelsTextColor[this.theme],
            fontSize: '11px',
            fontWeight: 'bold'
          }
        }
      }
    })
  }

  private generateStatesAnnotations(): XAxisAnnotationType[] {
    return this.variableData.times.map((x, index) => {
      const value: number = this.variableData.values[index]
      const match = this.variableData.colors.find((x) => x.value === value)
      const color: string | undefined = match?.color
      return {
        x: new Date(`${x}T00:00`).getTime(),
        borderColor: 'transparent',
        name: 'state',
        label_value: match?.label,
        label: {
          borderColor: '#00E396',
          borderWidth: 0,
          orientation: 'horizontal',
          text: match?.label ? this.translate(match.label) : '',
          offsetY: 50,
          style: {
            background: this.labelsBackgroundColor,
            color: color,
            fontSize: '9px',
            fontWeight: 'bold'
          }
        }
      }
    })
  }

  private generateTodayHighlightAnnotation(): XAxisAnnotationType {
    const x1 = new Date(this.variableData.times[0] + 'T00:00')
    const x2 = new Date(this.variableData.times[0] + 'T00:00')

    x1.setHours(x1.getHours() - 12)
    x2.setHours(x2.getHours() + 12)

    return {
      x: x1.getTime(),
      x2: x2.getTime(),
      fillColor: this.highlightColor
    }
  }
}

/*
const chartOptions = ref(
    {
        chart: {
            id: "weather_chart",
            sharedVariables: [],
            title: [],
            group: "weather_data",
            height: 250,
            width: 480,
            type: "line",
            background: "rgb(var(--v-theme-dark-color))",
            zoom: { enabled: false },
            foreColor: "#c0bfbf",
            toolbar: {
                show: false
            },
            animations: {
                enabled: true,
                easing: "easeinout",
                speed: 800,
                animateGradually: {
                    enabled: false,
                    delay: 0
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 800
                }
            }
        },
        colors: [],
        dataLabels: {
            enabled: false,
            enabledOnSeries: []
        },
        stroke: {
            curve: ["straight"],
            dashArray: [0]
        },
        title: {
            text: undefined,
            align: "center"
        },
        grid: {
            show: false,
        },
        markers: {
            size: [0],
            shape: ["square"],
            hover: {
                size: 0
            },
            showNullDataPoints: false,
        },
        xaxis: {
            type: 'datetime',
            categories: [
                new Date("2024-01-22T00:00").getTime(),
                new Date("2024-01-23T00:00").getTime(),
                new Date("2024-01-24T00:00").getTime(),
                new Date("2024-01-25T00:00").getTime(),
                new Date("2024-01-26T00:00").getTime(),
                new Date("2024-01-27T00:00").getTime(),
                new Date("2024-01-28T00:00").getTime(),
                new Date("2024-01-29T00:00").getTime(),
                new Date("2024-01-30T00:00").getTime()
            ],
            labels: {
                show: false
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: [
            {
                show: false,
                axisTicks: { show: false },
                axisBorder: { show: false },
                      tickAmount: 9,
                reversed: false,
                min: 0,
                max: 8
            }
        ],
        theme: {},
        annotations: {
            xaxis: [
                {
                    x: new Date("2024-01-23T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Sun',
                        offsetY: 10,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px'
                        }
                    }
                },
                {
                    x: new Date("2024-01-23T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: '22/01',
                        offsetY: 25,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-23T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Moderate',
                        offsetY: 50,
                        style: {
                            background: '#1f1f1f',
                            color: "#a3be87",
                            fontSize: '9px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-22T12:00").getTime(),
                    x2: new Date("2024-01-23T12:00").getTime(),
                    fillColor: 'rgba(192,191,191,0.5)',
                },
                {
                    x: new Date("2024-01-24T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Mon',
                        offsetY: 10,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px'
                        }
                    }
                },
                {
                    x: new Date("2024-01-24T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: '23/01',
                        offsetY: 25,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-24T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Low',
                        offsetY: 50,
                        style: {
                            background: '#1f1f1f',
                            color: "#78b4cb",
                            fontSize: '9px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-25T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Tue',
                        offsetY: 10,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px'
                        }
                    }
                },
                {
                    x: new Date("2024-01-25T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: '24/01',
                        offsetY: 25,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-25T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Low',
                        offsetY: 50,
                        style: {
                            background: '#1f1f1f',
                            color: "#78b4cb",
                            fontSize: '9px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-26T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Wed',
                        offsetY: 10,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px'
                        }
                    }
                },
                {
                    x: new Date("2024-01-26T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: '25/01',
                        offsetY: 25,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-26T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Moderate',
                        offsetY: 50,
                        style: {
                            background: '#1f1f1f',
                            color: "#a3be87",
                            fontSize: '9px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-27T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Thu',
                        offsetY: 10,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px'
                        }
                    }
                },
                {
                    x: new Date("2024-01-27T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: '26/01',
                        offsetY: 25,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-27T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'High',
                        offsetY: 50,
                        style: {
                            background: '#1f1f1f',
                            color: "#e8e08b",
                            fontSize: '9px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-28T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Fri',
                        offsetY: 10,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px'
                        }
                    }
                },
                {
                    x: new Date("2024-01-28T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: '27/01',
                        offsetY: 25,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-28T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Very High',
                        offsetY: 50,
                        style: {
                            background: '#1f1f1f',
                            color: "#e2b38f",
                            fontSize: '9px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-29T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Sat',
                        offsetY: 10,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px'
                        }
                    }
                },
                {
                    x: new Date("2024-01-29T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: '28/01',
                        offsetY: 25,
                        style: {
                            background: '#1f1f1f',
                            color: '#c0bfbf',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                },
                {
                    x: new Date("2024-01-29T00:00").getTime(),
                    borderColor: 'transparent',
                    label: {
                        borderColor: '#00E396',
                        borderWidth: 0,
                        orientation: 'horizontal',
                        text: 'Severe',
                        offsetY: 50,
                        style: {
                            background: '#1f1f1f',
                            color: "#c97c82",
                            fontSize: '9px',
                            fontWeight: 'bold'
                        }
                    }
                },
            ],
            yaxis: [],
        },
        tooltip: {
            enabled: false,
        },
        language: "english",
        legend: {
            show: false,
            showForSingleSeries: false
        },
        fill: {
            type: "gradient",
            gradient: {
                type: "vertical",
                colorStops: gradientCalc.getColorStops(
                    [2, 2, 1, 1, 2, 3, 4, 5, 5],
                    [
                        {
                            color: "#c97c82",
                            value: 5,
                            order: 5
                        },
                        {
                            color: "#e2b38f",
                            value: 4,
                            order: 4
                        },
                        {
                            color: "#e8e08b",
                            value: 3,
                            order: 3
                        },
                        {
                            color: "#a3be87",
                            value: 2,
                            order: 2
                        },
                        {
                            color: "#78b4cb",
                            value: 1,
                            order: 1
                        }
                    ]
                ),
            },
        }
    } as ChartOptions
)
const weatherSeries = ref(
    [
        {
            name: "prueba.json",
            data: [2, 2, 1, 1, 2, 3, 4, 5, 5]
        }
    ]
)


const sdfiGradient = [
    "#c97c82",
    "#e2b38f",
    "#f2eec4",
    "#d6e8c4",
    "#93c1c0",
    "#78b4cb",
]

const ercGradient = [
  "#b45563",
  "#d88471",
  "#efbf92",
  "#fbf0c5",
  "#daeaaf",
  "#a7ce92",
]

const biGradient = [
  "#ce72b0",
  "#e3b6d5",
  "#f2e6ee",
  "#e9f1dd",
  "#bedb9a",
  "#98c576",
]
 */
